// One version under review (E31-S6-T3, first cut: a single version, no side-by-side compare; ADR-033),
// ported from mockups/script-review.html's review pane: the title's chips and facts, the first-version
// callout, the script and knowledge check in two tabs, and the decision panel beside them (under them
// on a narrower screen). The scripts page shows it for the title picked in its list; the review page
// shows it on its own.
import type { Principal } from "@waypoint/contracts";
import { Button, cn, focusRing } from "@waypoint/surface";
import { useRef, useState, type KeyboardEvent } from "react";
import { Link } from "wouter";
import { t } from "../../i18n/t.js";
import { StatusChip } from "../../ui/form.js";
import { canEdit, editPath } from "../../versions/edit.js";
import { isDrafter, stageOf, type VersionDetail } from "../../versions/model.js";
import type { Acted } from "../../versions/queries.js";
import { guideName } from "./issue-format.js";
import { mmss } from "./plan-model.js";
import { Decision } from "./review-decision.js";
import { questionCount, Questions, Scenes } from "./review-script.js";
import { Seg, StageChip, type TitleInfo } from "./script-format.js";

export const CARD =
  "rounded-12 border border-wp-line bg-linear-to-b from-wp-panel-2/82 to-wp-panel/74";

type Tab = "script" | "questions";
const TABS: readonly Tab[] = ["script", "questions"];

function Facts({ version, info }: { version: VersionDetail; info: TitleInfo }) {
  const dt = "text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase";
  const citation = version.script.scenes[0]?.citation;
  const measured = version.render?.durationSeconds;
  const questions = questionCount(version.questions);
  const facts: [string, string][] = [
    ...(citation === undefined
      ? []
      : [
          [
            t("review.facts.guide"),
            t("review.facts.guideValue", {
              guide: guideName(citation.doc),
              edition: citation.docVersion,
            }),
          ] as [string, string],
        ]),
    [
      t("review.facts.runtime"),
      measured === undefined
        ? t("review.facts.estimated", { time: mmss(info.planned) })
        : t("review.facts.measured", { time: mmss(measured) }),
    ],
    [t("review.facts.scenes"), String(version.script.scenes.length)],
    ...(questions === undefined
      ? []
      : [[t("review.facts.questions"), String(questions)] as [string, string]]),
  ];
  return (
    <dl className="m-0 flex flex-wrap gap-x-5.5 gap-y-1.5 text-13 text-wp-text-2">
      {facts.map(([term, value]) => (
        <div key={term} className="flex items-baseline gap-1.5">
          <dt className={dt}>{term}</dt>
          <dd className="m-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Tabs({ version }: { version: VersionDetail }) {
  const [tab, setTab] = useState<Tab>("script");
  const refs = useRef<Record<Tab, HTMLButtonElement | null>>({ script: null, questions: null });
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.indexOf(tab);
    const next =
      e.key === "ArrowRight"
        ? TABS[(i + 1) % TABS.length]
        : e.key === "ArrowLeft"
          ? TABS[(i + TABS.length - 1) % TABS.length]
          : e.key === "Home"
            ? TABS[0]
            : e.key === "End"
              ? TABS[TABS.length - 1]
              : undefined;
    if (next === undefined) return;
    e.preventDefault();
    setTab(next);
    refs.current[next]?.focus();
  };
  return (
    <div className="px-4 pt-1.5 pb-4">
      <div
        role="tablist"
        aria-label={t("review.parts")}
        className="mb-3.5 flex gap-1 border-b border-wp-line"
      >
        {TABS.map((id) => (
          <button
            key={id}
            ref={(el) => {
              refs.current[id] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-controls={`panel-${id}`}
            aria-selected={tab === id}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => {
              setTab(id);
            }}
            onKeyDown={onKey}
            className={cn(
              "-mb-px cursor-pointer border-0 border-b-2 bg-transparent px-3 py-2 font-sans text-13 font-semibold",
              tab === id
                ? "border-wp-accent text-wp-text"
                : "border-transparent text-wp-text-2 hover:text-wp-text",
              focusRing,
            )}
          >
            {t(id === "script" ? "review.tab.script" : "review.tab.questions")}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {tab === "script" ? (
          <Scenes version={version} />
        ) : (
          <Questions markdown={version.questions} />
        )}
      </div>
    </div>
  );
}

interface Props {
  readonly version: VersionDetail;
  readonly info: TitleInfo;
  readonly principal: Principal;
  readonly onActed: (acted: Acted) => void;
  /** 1 on the review page of its own; 2 inside the scripts page, under its heading. */
  readonly level: 1 | 2;
}

export function ReviewPane({ version, info, principal, onActed, level }: Props) {
  const Title = level === 1 ? "h1" : "h2";
  const stage = stageOf(version, info.held);
  return (
    <div className="grid min-w-0 gap-4">
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Seg id={version.titleId} />
          <StageChip
            stage={stage}
            drafter={isDrafter(version, principal.userId)}
            {...(version.render ? { seconds: version.render.durationSeconds } : {})}
          />
          <StatusChip tone="quiet">
            {t(version.number === 1 ? "review.versionFirst" : "review.version", {
              n: version.number,
            })}
          </StatusChip>
        </div>
        <Title
          id={level === 1 ? "page-title" : "review-title"}
          className="m-0 text-[clamp(1.3rem,2.6vw,1.7rem)] leading-tight font-bold"
        >
          {info.title}
        </Title>
        <Facts version={version} info={info} />
        {version.number === 1 ? (
          <div className="rounded-8 border border-wp-line-strong bg-wp-bg/58 px-3 py-2.5 text-13 text-wp-text-2">
            {t("review.first")}
          </div>
        ) : null}
        <p className="m-0 text-13">
          <Link
            href={`/content/titles/${encodeURIComponent(version.titleId)}/versions`}
            className={cn("text-wp-accent underline underline-offset-2", focusRing)}
          >
            {t("scripts.versions")}
          </Link>
        </p>
        {version.id !== undefined && canEdit(version, principal) ? (
          <Button asChild compact variant="primary" className="justify-self-start">
            <Link href={editPath(version.id)}>{t("edit.open")}</Link>
          </Button>
        ) : null}
      </div>
      <div className="grid items-start gap-5 min-[1181px]:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]">
        <div className={cn(CARD, "min-w-0")}>
          <Tabs key={version.id} version={version} />
        </div>
        <aside
          aria-labelledby="decision-heading"
          className={cn(
            CARD,
            "grid min-w-0 gap-3 px-4 py-3.5 min-[1181px]:sticky min-[1181px]:top-4",
          )}
        >
          <Decision
            key={`${version.id ?? ""}-${version.state}`}
            version={version}
            info={info}
            userId={principal.userId}
            onActed={onActed}
          />
        </aside>
      </div>
    </div>
  );
}
